<template>
  <div>
    <v-dialog v-model="dialogOpen" fullscreen scrim="white" no-click-animation class="teacher-timer-dialog">
      <div class="teacher-timer-overlay" data-testid="TeacherTimerModal-overlay">
        <div class="teacher-timer-actions">
          <button
            type="button"
            class="teacher-timer-action"
            :class="{ muted: !timerStore.soundEnabled }"
            data-testid="TeacherTimerModal-sound"
            :aria-label="soundLabel"
            @click="toggleSound"
          >
            <v-icon size="16">{{ soundIcon }}</v-icon>
          </button>
          <button
            type="button"
            class="teacher-timer-action"
            data-testid="TeacherTimerModal-minimize"
            aria-label="Samazināt taimeri"
            @click="timerStore.minimized = true"
          >
            <v-icon size="16">fa-light fa-down-left-and-up-right-to-center</v-icon>
          </button>
        </div>

        <div class="teacher-timer-card">
          <h1 class="teacher-timer-title">Atlikušais laiks</h1>

          <v-progress-circular
            :size="340"
            :width="22"
            :model-value="timerStore.progressPercent"
            :color="ringColor"
            class="teacher-timer-ring"
          >
            <span class="teacher-timer-time" data-testid="TeacherTimerModal-time">{{ formattedTime }}</span>
          </v-progress-circular>

          <div class="teacher-timer-inputs">
            <div class="teacher-timer-input-group">
              <label for="teacher-timer-minutes">MIN.</label>
              <input
                id="teacher-timer-minutes"
                type="text"
                inputmode="numeric"
                maxlength="2"
                :value="inputMinutes"
                :disabled="timerStore.isCountingDown"
                data-testid="TeacherTimerModal-minutes"
                @change="onMinutesChange"
              />
            </div>
            <span class="teacher-timer-inputs-colon">:</span>
            <div class="teacher-timer-input-group">
              <label for="teacher-timer-seconds">SEK.</label>
              <input
                id="teacher-timer-seconds"
                type="text"
                inputmode="numeric"
                maxlength="2"
                :value="inputSeconds"
                :disabled="timerStore.isCountingDown"
                data-testid="TeacherTimerModal-seconds"
                @change="onSecondsChange"
              />
            </div>
          </div>

          <div class="teacher-timer-controls">
            <button
              type="button"
              class="teacher-timer-control"
              data-testid="TeacherTimerModal-reset"
              aria-label="Atiestatīt taimeri"
              @click="timerStore.reset()"
            >
              <v-icon size="20">fa-light fa-rotate-left</v-icon>
            </button>
            <button
              type="button"
              class="teacher-timer-control play"
              :disabled="timerStore.durationSeconds === 0"
              data-testid="TeacherTimerModal-playPause"
              :aria-label="timerStore.status === 'running' ? 'Pauzēt taimeri' : 'Uzsākt taimeri'"
              @click="togglePlay"
            >
              <v-icon size="28">{{ timerStore.status === "running" ? "fa-solid fa-pause" : "fa-solid fa-play" }}</v-icon>
            </button>
            <button
              type="button"
              class="teacher-timer-control"
              :disabled="!timerStore.isCountingDown"
              data-testid="TeacherTimerModal-stop"
              aria-label="Apturēt taimeri"
              @click="timerStore.stopToIdle()"
            >
              <v-icon size="20">fa-light fa-square</v-icon>
            </button>
          </div>
        </div>
      </div>
    </v-dialog>

    <Teleport to="body">
      <div
        v-if="timerStore.open && timerStore.minimized"
        ref="mini"
        class="teacher-timer-mini"
        :class="{ dragging: isDragging }"
        :style="{ transform: `translate(${dragPosition.x}px, ${dragPosition.y}px)` }"
        data-testid="TeacherTimerMini-widget"
      >
        <button
          type="button"
          class="teacher-timer-mini-drag"
          data-testid="TeacherTimerMini-drag"
          aria-label="Pārvietot taimeri"
          @pointerdown.prevent="startDrag"
        >
          <v-icon size="14">fa-regular fa-grip-dots-vertical</v-icon>
        </button>

        <v-progress-circular
          :size="64"
          :width="5"
          :model-value="timerStore.progressPercent"
          :color="ringColor"
          class="teacher-timer-ring"
        >
          <span class="teacher-timer-mini-time" data-testid="TeacherTimerMini-time">{{ formattedTime }}</span>
        </v-progress-circular>

        <div class="teacher-timer-mini-controls">
          <button
            type="button"
            class="teacher-timer-control play mini"
            :disabled="timerStore.durationSeconds === 0"
            data-testid="TeacherTimerMini-playPause"
            :aria-label="timerStore.status === 'running' ? 'Pauzēt taimeri' : 'Uzsākt taimeri'"
            @click="togglePlay"
          >
            <v-icon size="13">{{ timerStore.status === "running" ? "fa-solid fa-pause" : "fa-solid fa-play" }}</v-icon>
          </button>
          <button
            type="button"
            class="teacher-timer-control mini"
            data-testid="TeacherTimerMini-stop"
            aria-label="Izslēgt taimeri"
            @click="timerStore.closeTimer()"
          >
            <v-icon size="11">fa-light fa-square</v-icon>
          </button>
          <button
            type="button"
            class="teacher-timer-control mini"
            :class="{ muted: !timerStore.soundEnabled }"
            data-testid="TeacherTimerMini-sound"
            :aria-label="soundLabel"
            @click="toggleSound"
          >
            <v-icon size="11">{{ soundIcon }}</v-icon>
          </button>
          <button
            type="button"
            class="teacher-timer-control mini"
            data-testid="TeacherTimerMini-expand"
            aria-label="Palielināt taimeri"
            @click="timerStore.minimized = false"
          >
            <v-icon size="11">fa-light fa-up-right-and-down-left-from-center</v-icon>
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import clamp from "lodash/clamp"
import padStart from "lodash/padStart"

import confetti from "canvas-confetti"
import timerEndSound from "~/assets/sounds/timer-end.mp3"
import { useTeacherToolsScrimClip } from "~/composables/useTeacherToolsScrimClip"
import { useTeacherTimerStore } from "~/stores/teacherTimerStore"

let finishAudio: HTMLAudioElement | null = null

export default {
  setup() {
    const timerStore = useTeacherTimerStore()
    const scrimClip = useTeacherToolsScrimClip(".teacher-timer-dialog")
    return { timerStore, ...scrimClip }
  },

  data() {
    return {
      isDragging: false,
      dragPosition: { x: 0, y: 0 },
      dragOrigin: { x: 0, y: 0 },
      dragBounds: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
    }
  },

  computed: {
    dialogOpen: {
      get(): boolean {
        return this.timerStore.open && !this.timerStore.minimized
      },
      set(value: boolean) {
        if (!value) {
          this.timerStore.yieldToOtherTool()
        }
      },
    },

    ringColor(): string {
      return this.timerStore.isDanger ? "#EF4444" : "#F9D805"
    },

    formattedTime(): string {
      return `${this.pad(Math.floor(this.timerStore.remainingSeconds / 60))}:${this.pad(this.timerStore.remainingSeconds % 60)}`
    },

    inputTotalSeconds(): number {
      return this.timerStore.isCountingDown ? this.timerStore.remainingSeconds : this.timerStore.durationSeconds
    },

    inputMinutes(): string {
      return this.pad(Math.floor(this.inputTotalSeconds / 60))
    },

    inputSeconds(): string {
      return this.pad(this.inputTotalSeconds % 60)
    },

    soundIcon(): string {
      return this.timerStore.soundEnabled ? "fa-light fa-volume" : "fa-sharp fa-light fa-volume-xmark"
    },

    soundLabel(): string {
      return this.timerStore.soundEnabled ? "Izslēgt skaņu" : "Ieslēgt skaņu"
    },
  },

  watch: {
    dialogOpen(isOpen: boolean) {
      if (isOpen) {
        this.$nextTick(() => this.startScrimClipObserver())
      } else {
        this.stopScrimClipObserver()
      }
    },

    "timerStore.open"(isOpen: boolean) {
      if (isOpen) {
        this.primeFinishSound()
      }
    },

    "timerStore.finishCount"() {
      this.fireConfetti()
      this.playFinishSound()
    },
  },

  unmounted() {
    this.stopDrag()
    this.stopScrimClipObserver()
    this.timerStore.clearTick()
    this.stopFinishSound()
  },

  methods: {
    pad(value: number): string {
      return padStart(value.toString(), 2, "0")
    },

    toggleSound() {
      this.timerStore.toggleSound()
      if (finishAudio) {
        finishAudio.muted = !this.timerStore.soundEnabled
      }
    },

    primeFinishSound(): HTMLAudioElement {
      if (!finishAudio) {
        finishAudio = new Audio(timerEndSound)
        finishAudio.preload = "auto"
        finishAudio.load()
      }
      return finishAudio
    },

    playFinishSound() {
      if (!this.timerStore.soundEnabled) {
        return
      }
      const audio = this.primeFinishSound()
      audio.muted = false
      audio.currentTime = 0
      audio.play().catch(() => undefined)
    },

    stopFinishSound() {
      if (!finishAudio) {
        return
      }
      finishAudio.pause()
      finishAudio.currentTime = 0
    },

    togglePlay() {
      if (this.timerStore.status === "running") {
        this.timerStore.pause()
      } else {
        this.timerStore.start()
      }
    },

    onMinutesChange(event: Event) {
      const input = event.target as HTMLInputElement
      const minutes = Math.min(Math.max(parseInt(input.value, 10) || 0, 0), 99)
      this.timerStore.setDuration(minutes * 60 + (this.timerStore.durationSeconds % 60))
      input.value = this.pad(minutes)
    },

    onSecondsChange(event: Event) {
      const input = event.target as HTMLInputElement
      const seconds = Math.min(Math.max(parseInt(input.value, 10) || 0, 0), 59)
      this.timerStore.setDuration(Math.floor(this.timerStore.durationSeconds / 60) * 60 + seconds)
      input.value = this.pad(seconds)
    },

    startDrag(event: PointerEvent) {
      const rect = (this.$refs.mini as HTMLElement).getBoundingClientRect()
      const left = rect.left - this.dragPosition.x
      const top = rect.top - this.dragPosition.y

      this.isDragging = true
      this.dragOrigin = { x: event.clientX - this.dragPosition.x, y: event.clientY - this.dragPosition.y }
      this.dragBounds = {
        minX: -left,
        maxX: window.innerWidth - rect.width - left,
        minY: -top,
        maxY: window.innerHeight - rect.height - top,
      }

      window.addEventListener("pointermove", this.onDrag)
      window.addEventListener("pointerup", this.stopDrag)
    },

    onDrag(event: PointerEvent) {
      this.dragPosition = {
        x: clamp(event.clientX - this.dragOrigin.x, this.dragBounds.minX, this.dragBounds.maxX),
        y: clamp(event.clientY - this.dragOrigin.y, this.dragBounds.minY, this.dragBounds.maxY),
      }
    },

    stopDrag() {
      this.isDragging = false
      window.removeEventListener("pointermove", this.onDrag)
      window.removeEventListener("pointerup", this.stopDrag)
    },

    fireConfetti() {
      const burst = { particleCount: 100, spread: 75, zIndex: 3000 }

      confetti({ ...burst, angle: 60, origin: { x: 0, y: 0.6 } })
      confetti({ ...burst, angle: 120, origin: { x: 1, y: 0.6 } })
      confetti({ ...burst, particleCount: 160, spread: 100, origin: { x: 0.5, y: 0.4 } })
    },
  },
}
</script>

<style lang="sass" scoped>
.teacher-timer-dialog :deep(.v-overlay__scrim)
  background: rgba(255, 255, 255, 0.75) !important
  opacity: 1 !important

  &::before
    content: ""
    position: absolute
    inset: 0
    backdrop-filter: blur(8px)
    z-index: -1

.teacher-timer-dialog :deep(.v-overlay__content)
  background: transparent !important
  pointer-events: none

.teacher-timer-overlay
  width: 100vw
  height: 100vh
  display: flex
  align-items: center
  justify-content: center
  pointer-events: none

.teacher-timer-actions
  position: absolute
  top: 72px
  right: 16px
  display: flex
  align-items: center
  gap: 8px

.teacher-timer-action
  width: 36px
  height: 36px
  border-radius: 50%
  border: 2px solid #E5E7EB
  background-color: white
  color: #6B7280
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer
  pointer-events: auto
  transition: color 0.15s, border-color 0.15s

  &:hover
    color: #111827

  &.muted
    border-color: #EF4444
    color: #EF4444

.teacher-timer-card
  display: flex
  flex-direction: column
  align-items: center
  gap: 40px
  pointer-events: auto

.teacher-timer-title
  font-size: 1.1rem
  font-weight: 500
  color: #6B7280
  letter-spacing: 0.08em
  text-transform: uppercase

.teacher-timer-ring :deep(.v-progress-circular__underlay)
  stroke: #E5E7EB

.teacher-timer-card .teacher-timer-ring :deep(.v-progress-circular__content)
  position: absolute
  inset: 24px
  background-color: #FFFFFF
  border-radius: 50%

.teacher-timer-ring :deep(.v-progress-circular__overlay)
  transition: stroke-dashoffset 0.6s, stroke 0.6s

.teacher-timer-time
  font-size: 4rem
  font-weight: 700
  letter-spacing: -0.04em
  color: #111827
  font-variant-numeric: tabular-nums

.teacher-timer-inputs
  display: flex
  align-items: center
  gap: 8px

.teacher-timer-inputs-colon
  font-size: 2rem
  font-weight: 700
  color: #D1D5DB
  margin-top: 20px

.teacher-timer-input-group
  display: flex
  flex-direction: column
  align-items: center
  gap: 4px

  label
    font-size: 0.7rem
    color: #9CA3AF
    font-weight: 500
    letter-spacing: 0.06em

  input
    width: 72px
    height: 52px
    border-radius: 12px
    border: 2px solid #E5E7EB
    background-color: white
    text-align: center
    font-size: 1.5rem
    font-weight: 700
    color: #111827
    outline: none
    font-variant-numeric: tabular-nums
    transition: border-color 0.2s

    &:focus
      border-color: #F9D805

    &:disabled
      background-color: #F3F4F6
      border-color: #F3F4F6

.teacher-timer-controls
  display: flex
  align-items: center
  gap: 16px

.teacher-timer-control
  width: 52px
  height: 52px
  border-radius: 50%
  border: 2px solid #E5E7EB
  background-color: white
  color: #6B7280
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer
  transition: border-color 0.15s, color 0.15s

  &:hover:not(:disabled)
    border-color: #F9D805
    color: #111827

  &:disabled
    background-color: #F3F4F6
    border-color: #F3F4F6
    color: #D1D5DB
    cursor: default

  &.muted
    border-color: #EF4444
    color: #EF4444

  &.play
    width: 72px
    height: 72px
    border: none
    background-color: #F9D805
    color: #111827
    box-shadow: 0 4px 20px rgba(249, 216, 5, 0.4)

    &:disabled
      background-color: #F3F4F6
      color: #D1D5DB
      box-shadow: none

  &.mini
    width: 28px
    height: 28px

    &.play
      width: 32px
      height: 32px
      box-shadow: none

.teacher-timer-mini
  position: fixed
  top: 68px
  right: 16px
  z-index: 2600
  background-color: white
  border-radius: 20px
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12)
  padding: 10px 14px 10px 10px
  display: flex
  align-items: center
  gap: 10px

.teacher-timer-mini.dragging
  cursor: grabbing
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18)

.teacher-timer-mini-drag
  display: flex
  align-items: center
  justify-content: center
  width: 20px
  height: 28px
  border: none
  background: none
  color: #9CA3AF
  cursor: move
  touch-action: none
  transition: color 0.15s

  &:hover
    color: #111827

.teacher-timer-mini-time
  font-size: 10px
  font-weight: 700
  color: #111827
  font-variant-numeric: tabular-nums

.teacher-timer-mini-controls
  display: flex
  align-items: center
  gap: 6px
</style>
